import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CalendarClock, PenLine, Printer, Search, Loader2 } from 'lucide-react';

const daysUntil = (iso) => Math.ceil((new Date(iso) - new Date()) / 86400000);

function StatusBadge({ iso, id }) {
  const d = daysUntil(iso);
  const cls = d < 0 ? 'bg-[#991B1B]/10 text-[#991B1B]' : d <= 7 ? 'bg-[#CA8A04]/15 text-[#92400E]' : 'bg-[#166534]/10 text-[#166534]';
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${cls}`} data-testid={`due-status-${id}`}>{d < 0 ? `Overdue ${-d}d` : d === 0 ? 'Due today' : `Due in ${d}d`}</span>;
}

function Stat({ label, value, tone, active, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`text-left p-4 rounded-xl border transition-colors ${active ? 'border-[#0E7490] bg-[#F0FDFA]' : 'border-[#E2E8F0] bg-white hover:border-[#0E7490]/40'}`}>
      <div className={`text-2xl font-semibold ${tone}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</div>
      <div className="text-xs text-[#64748B] mt-0.5">{label}</div>
    </button>
  );
}

function Row({ r }) {
  const navigate = useNavigate();
  const pdf = r.print_layout === 'pdf_original';
  const fill = pdf ? `/forms/${r.form_id}/sign-on-pdf?patientId=${r.patient_id}` : `/forms/${r.form_id}/fill?patientId=${r.patient_id}`;
  return (
    <tr className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC] transition-colors" data-testid={`due-row-${r.id}`}>
      <td className="py-3 px-3"><Link to={`/patients/${r.patient_id}?formRecord=${r.id}`} className="font-medium text-[#0A2342] hover:text-[#0E7490]">{r.patient_name || 'Patient'}</Link></td>
      <td className="py-3 px-3 text-sm text-[#334155]">{r.form_name}</td>
      <td className="py-3 px-3 text-xs text-[#64748B]">{new Date(r.last_updated_at).toLocaleDateString()}<div>{r.last_updated_by_name}</div></td>
      <td className="py-3 px-3 text-sm text-[#334155] whitespace-nowrap">{new Date(r.next_due_at).toLocaleDateString()}</td>
      <td className="py-3 px-3"><StatusBadge iso={r.next_due_at} id={r.id} /></td>
      <td className="py-3 px-3">
        <div className="flex gap-1.5 justify-end">
          <Button size="sm" className="careiq-btn-primary h-8" onClick={() => navigate(fill)} data-testid={`due-update-${r.id}`}><PenLine className="w-3.5 h-3.5 mr-1" />Update</Button>
          {pdf && <Button size="sm" variant="outline" className="h-8" onClick={() => window.open(`/forms/${r.form_id}/print-original?submissionId=${r.last_submission_id}`, '_blank')} data-testid={`due-view-${r.id}`}><Printer className="w-3.5 h-3.5" /></Button>}
        </div>
      </td>
    </tr>
  );
}

export default function DueFormsBoard() {
  const [rows, setRows] = useState(null);
  const [days, setDays] = useState('7');
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');

  useEffect(() => {
    setRows(null);
    api.get('/form-records/upcoming', { params: { days } }).then(r => setRows(r.data.records || [])).catch(() => setRows([]));
  }, [days]);

  const overdue = (rows || []).filter(r => daysUntil(r.next_due_at) < 0);
  const soon = (rows || []).filter(r => daysUntil(r.next_due_at) >= 0);
  const shown = useMemo(() => {
    const base = filter === 'overdue' ? overdue : filter === 'soon' ? soon : rows || [];
    const term = q.trim().toLowerCase();
    return term ? base.filter(r => `${r.patient_name} ${r.form_name}`.toLowerCase().includes(term)) : base;
  }, [rows, filter, q, overdue, soon]);

  return (
    <div className="space-y-6" data-testid="due-forms-board">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}><CalendarClock className="w-7 h-7 text-[#0E7490]" />Due Forms</h1>
          <p className="text-[#64748B] mt-1">Patient forms that are overdue or coming due — update them in one tap.</p>
        </div>
        <div className="flex gap-2">
          <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[#94A3B8]" /><Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search patient or form" className="pl-8 w-56" data-testid="due-search-input" /></div>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-40" data-testid="due-window-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Due within 7 days</SelectItem>
              <SelectItem value="14">Due within 14 days</SelectItem>
              <SelectItem value="30">Due within 30 days</SelectItem>
              <SelectItem value="60">Due within 60 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 max-w-xl">
        <Stat label="All shown" value={(rows || []).length} tone="text-[#0A2342]" active={filter === 'all'} onClick={() => setFilter('all')} testid="due-filter-all" />
        <Stat label="Overdue" value={overdue.length} tone="text-[#991B1B]" active={filter === 'overdue'} onClick={() => setFilter('overdue')} testid="due-filter-overdue" />
        <Stat label="Coming due" value={soon.length} tone="text-[#92400E]" active={filter === 'soon'} onClick={() => setFilter('soon')} testid="due-filter-soon" />
      </div>
      <Card className="careiq-card">
        <CardContent className="p-0 overflow-x-auto">
          {rows === null ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#0E7490]" /></div>
          ) : shown.length === 0 ? (
            <div className="text-center py-12 text-sm text-[#94A3B8]" data-testid="due-empty">Nothing due in this window.</div>
          ) : (
            <table className="w-full text-left">
              <thead><tr className="text-[11px] uppercase tracking-wider text-[#64748B] border-b border-[#E2E8F0]">
                <th className="py-2.5 px-3">Patient</th><th className="py-2.5 px-3">Form</th><th className="py-2.5 px-3">Last updated</th><th className="py-2.5 px-3">Next due</th><th className="py-2.5 px-3">Status</th><th className="py-2.5 px-3" />
              </tr></thead>
              <tbody>{shown.map(r => <Row key={r.id} r={r} />)}</tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
